import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { CreditCard, Banknote, Check, Landmark } from 'lucide-react';
import { toast } from 'sonner';
import { SepaDebitForm } from './SepaDebitForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentMethodSelector = ({ 
  open, 
  onClose, 
  lookupKey, 
  serviceName, 
  isSubscription = false 
}) => {
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSepa, setShowSepa] = useState(false);

  const handleClose = () => {
    setShowSepa(false);
    onClose();
  };

  const handlePayment = async (provider) => {
    setLoading(true);
    setSelectedProvider(provider);
    
    try {
      let endpoint, requestBody;
      
      if (provider === 'stripe') {
        endpoint = isSubscription ? '/api/subscriptions/create' : '/api/payments/checkout';
        requestBody = {
          lookup_key: lookupKey,
          quantity: isSubscription ? undefined : 1,
          origin_url: window.location.origin,
          customer_email: 'client@example.com',
          customer_name: 'Client',
        };
      } else if (provider === 'mollie') {
        endpoint = '/api/payments/mollie/checkout';
        requestBody = {
          lookup_key: lookupKey,
          quantity: 1,
          origin_url: window.location.origin,
          customer_email: 'client@example.com',
          customer_name: 'Client',
        };
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || 'Erreur lors de la création de la session');
      }
      
      // Redirect to checkout
      window.location.href = data.checkout_url;
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Erreur lors du paiement. Veuillez réessayer.');
      setLoading(false);
      setSelectedProvider(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-dark border-gold/30 text-white max-w-md">
        {showSepa ? (
          <SepaDebitForm
            lookupKey={lookupKey}
            serviceName={serviceName}
            isSubscription={isSubscription}
            onBack={() => setShowSepa(false)}
            onDone={handleClose}
          />
        ) : (
        <>
        <DialogHeader>
          <DialogTitle className="text-2xl font-playfair text-gold">
            Choisissez votre méthode de paiement
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Sélectionnez votre mode de paiement préféré pour <span className="text-white font-semibold">{serviceName}</span>
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Stripe Option */}
          <button
            onClick={() => handlePayment('stripe')}
            disabled={loading}
            className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
              selectedProvider === 'stripe' && loading
                ? 'border-gold bg-gold/10'
                : 'border-gold/30 hover:border-gold hover:bg-gold/5'
            } ${loading && selectedProvider !== 'stripe' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Stripe</h3>
                  <p className="text-sm text-gray-400">Cartes bancaires</p>
                </div>
              </div>
              {selectedProvider === 'stripe' && loading && (
                <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs bg-white/10 px-2 py-1 rounded">Visa</span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded">Mastercard</span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded">Amex</span>
            </div>
          </button>

          {/* Mollie Option */}
          <button
            onClick={() => handlePayment('mollie')}
            disabled={loading}
            className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
              selectedProvider === 'mollie' && loading
                ? 'border-gold bg-gold/10'
                : 'border-gold/30 hover:border-gold hover:bg-gold/5'
            } ${loading && selectedProvider !== 'mollie' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-pink-600 rounded-lg flex items-center justify-center">
                  <Banknote className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Mollie</h3>
                  <p className="text-sm text-gray-400">Toutes méthodes EU</p>
                </div>
              </div>
              {selectedProvider === 'mollie' && loading && (
                <div className="w-5 h-5 border-2 border-gold border-t-transparent rounded-full animate-spin" />
              )}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <span className="text-xs bg-white/10 px-2 py-1 rounded">iDEAL</span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded">Bancontact</span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded">PayPal</span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded">SEPA</span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded">Cartes</span>
              <span className="text-xs bg-white/10 px-2 py-1 rounded text-neon">+Plus</span>
            </div>
          </button>

          {/* SEPA Direct Debit Option */}
          {(
            <button
              onClick={() => setShowSepa(true)}
              disabled={loading}
              data-testid="sepa-method-btn"
              className={`w-full p-6 rounded-lg border-2 transition-all text-left border-gold/30 hover:border-gold hover:bg-gold/5 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-lg flex items-center justify-center">
                  <Landmark className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Prélèvement SEPA</h3>
                  <p className="text-sm text-gray-400">
                    {isSubscription ? 'Saisissez votre IBAN, prélèvement automatique chaque mois' : 'Saisissez votre IBAN, débit sous 2 à 5 jours'}
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gold/20">
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <Check className="w-4 h-4 text-neon" />
            <span>Paiement 100% sécurisé et crypté</span>
          </div>
        </div>
        </>
        )}
      </DialogContent>
    </Dialog>
  );
};
